import type { HTMLAttributes, ReactNode } from "react";
import { Lightbulb, X } from "lucide-react";
import { cn } from "../../lib/cn";

export interface HintCardProps extends Omit<HTMLAttributes<HTMLElement>, "title"> {
  /** Frase curta de abertura, em negrito. Opcional. */
  title?: ReactNode;
  /** A explicação, em linguagem de quem usa (nunca "sincronização", "API", "id"). */
  children: ReactNode;
  /** Fechar a dica. A tela guarda "fechada" por PESSOA (I2). */
  onDismiss: () => void;
  dismissLabel?: string;
  /** Enquanto o fechamento está sendo salvo. */
  dismissing?: boolean;
}

/**
 * Dica por tela (I2) — nunca modal, nunca no caminho.
 *
 * Bloco `sunken` (cinza dentro do canvas) com ícone + texto: não compete com o KPI nem com
 * a única cor de ação da tela. Fecha num toque, e o alvo do X tem 44 px no celular.
 * É uma `aside` com nome acessível: o leitor de tela anuncia como complementar, não como
 * parte do conteúdo principal.
 */
export function HintCard({
  title,
  children,
  onDismiss,
  dismissLabel = "Fechar dica",
  dismissing = false,
  className,
  ...props
}: HintCardProps) {
  return (
    <aside
      aria-label="Dica"
      className={cn(
        "flex items-start gap-3 rounded-md bg-surface-subtle p-3 text-sm ring-1 ring-inset ring-border",
        className,
      )}
      {...props}
    >
      <Lightbulb aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
      <div className="min-w-0 flex-1 text-muted-foreground">
        {title ? <p className="font-medium text-foreground">{title}</p> : null}
        <div>{children}</div>
      </div>
      <button
        type="button"
        aria-label={dismissLabel}
        onClick={onDismiss}
        disabled={dismissing}
        className="hit-44 -mr-1 -mt-1 inline-flex size-7 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
      >
        <X aria-hidden="true" className="size-4" />
      </button>
    </aside>
  );
}
